import { Colors } from "../consts/Colors.js";
import { Fonts } from "../consts/Fonts.js";
import AbstractScene from "./AbstractScene.js";
import ButtonRounded from "../prefabs/ButtonRounded.js";
import { Sizes } from "../consts/Sizes.js";

export default class Video extends AbstractScene {
  constructor() {
    super({ key: "Video" });
  }

  init(data) {
    this._init(data);
  }

  preload() {
    this._preload();
    this.videoKey = "videoLoreal";
    this.SLIDER_WIDTH = 1500;
    this.allControls = [];
  }

  update() {
    super.update();
    if (!this.video || !this.video.video) return;

    const nativeVideo = this.video.video; // élément HTMLVideoElement
    const duration = nativeVideo.duration;

    // ⏳ Mise à jour de la barre de buffering
    const buffered = nativeVideo.buffered;
    if (buffered && buffered.length > 0 && isFinite(duration)) {
      const end = buffered.end(buffered.length - 1);
      const percent = Phaser.Math.Clamp(end / duration, 0, 1);
      if (this.bufferBar) {
        this.bufferBar.width = percent * this.SLIDER_WIDTH;
      }
    }

    // 🕒 Lecture classique (ta logique actuelle)
    if (this.isPlaying && !this.isDraging) {
      const progress = this.video.getProgress();
      const duration = this.video.getDuration();
      const current = progress * duration;
      this.barFill.width = progress * this.SLIDER_WIDTH;
      this.slider.value = progress;
      this.sliderButton.x =
        this.sliderLine.x - this.SLIDER_WIDTH / 2 + this.barFill.width;
      this.currentTimeText.setText(this.formatTime(current));
      this.durationText.setText(this.formatTime(duration));
    } else if (this.isDraging) {
      const duration = this.video.getDuration();
      const current = this.slider.value * duration;
      this.barFill.width = this.slider.value * this.SLIDER_WIDTH;
      this.sliderButton.x =
        this.sliderLine.x - this.SLIDER_WIDTH / 2 + this.barFill.width;
      this.currentTimeText.setText(this.formatTime(current));
    }
  }

  create() {
    this.cameras.main.setBackgroundColor(Colors.Elmt.Blue);
    // Afficher l’image
    this.add.image(0, 0, "background").setOrigin(0);

    this.addVideo();
  }

  addVideo() {
    this.isDraging = false;
    this.isPlaying = false;
    this.isPaused = false;

    // 📹 1. Ajoute la vidéo
    const blocker = this.add
      .image(0, 0, "background")
      .setOrigin(0)
      .setVisible(false);

    this.video = this.add.video(0, 0, this.videoKey).setOrigin(0.5).setDepth(1);

    this.video.setPaused(true);

    // 2. On centre la vidéo au milieu du canvas
    this.video.setPosition(this.width / 2, this.height / 2 - 25);
    this.video.setScale(0.95); // Ajuste la taille de la vidéo si nécessaire

    // ▶️ 3. Bouton Play au centre
    this.startButton = this.add
      .image(this.width / 2, this.height / 2, "playCircle")
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .setDepth(3);
    this.startButton.setAlpha(1);
    this.allControls.push(this.startButton);

    // 🕹️ Animation au survol du bouton Play
    this.startButton.on("pointerover", () => {
      this.tweens.add({
        targets: this.startButton,
        scale: 1.1,
        duration: 200,
        ease: "Sine.easeInOut",
      });
    });

    this.startButton.on("pointerout", () => {
      this.tweens.add({
        targets: this.startButton,
        scale: 1,
        duration: 200,
        ease: "Sine.easeInOut",
      });
    });

    // Clique sur le bouton Play pour démarrer la vidéo
    this.startButton.on("pointerdown", () => {
      this.video.play();
      this.isPlaying = true;
      this.video.setMute(false);
      this.video.setVolume(1); // au cas où
      this.isPaused = false;
      this.startButton.setAlpha(0); // Cache le bouton play
      this.allControls.forEach((control) => control.setVisible(true));
    });

    this.backControls = this.add
      .rectangle(0, this.height, this.width, 52, Colors.Elmt.Gray)
      .setOrigin(0, 1)
      .setVisible(false);

    const yCoordinates = this.height - 25;
    this.allControls.push(this.backControls);

    // Boutton play/pause
    this.playPauseButton = this.add
      .image(90, yCoordinates, "pause")
      .setScale(0.8)
      .setVisible(false);
    this.allControls.push(this.playPauseButton);

    // ⏱️ Texte durée
    this.currentTimeText = this.add
      .text(150, yCoordinates, "0:00", {
        fontSize: Sizes.Text.p,
        color: Colors.Text.White,
        fontFamily: Fonts.Text.sub,
      })
      .setOrigin(0.5)
      .setDepth(20)
      .setVisible(false);
    this.allControls.push(this.currentTimeText);

    this.durationText = this.add
      .text(1760, yCoordinates, "0:00", {
        fontSize: Sizes.Text.p,
        color: Colors.Text.White,
        fontFamily: Fonts.Text.sub,
      })
      .setOrigin(0.5)
      .setVisible(false)
      .setDepth(20);
    this.allControls.push(this.durationText);

    const SLIDER_HALF_WIDTH = this.SLIDER_WIDTH / 2;

    this.sliderLine = this.add
      .rexRoundRectangle(
        this.width / 2,
        yCoordinates,
        this.SLIDER_WIDTH,
        6,
        3,
        Colors.Elmt.White
      )
      .setAlpha(0.5)
      .setVisible(false)
      .setDepth(20);
    this.allControls.push(this.sliderLine);
    this.barFill = this.add
      .rexRoundRectangle(
        this.width / 2 - SLIDER_HALF_WIDTH - 12,
        yCoordinates,
        0,
        6,
        3,
        Colors.Elmt.LightBlue
      )
      .setOrigin(0, 0.5)
      .setVisible(false)
      .setDepth(50);
    this.allControls.push(this.barFill);
    this.sliderLine.setSize(this.SLIDER_WIDTH, this.sliderLine.height);
    this.sliderLine.setInteractive({ useHandCursor: true });
    this.sliderLine.on("pointerdown", (event) => {
      let a = event.x;
      let b = this.sliderLine.x;
      let c = a - b;
      let value = (c + SLIDER_HALF_WIDTH) / this.SLIDER_WIDTH;
      this.barFill.width = value * this.SLIDER_WIDTH;
      this.slider.value = value;
      this.video.seekTo(value);
    });
    this.sliderButton = this.add
      .circle(
        this.width / 2 - SLIDER_HALF_WIDTH,
        yCoordinates,
        15,
        Colors.Elmt.White
      )
      .setInteractive({ useHandCursor: true })
      .setAlpha(1)
      .setVisible(false)
      .setDepth(50);
    this.allControls.push(this.sliderButton);
    this.sliderButton.setStrokeStyle(4, Colors.Elmt.White);
    this.sliderButton.on("pointerdown", () => {
      this.isDraging = true;
      this.isPlaying = false;
      this.video.setPaused(true);
    });
    this.sliderButton.on("pointerup", () => {
      this.isDraging = false;
      this.video.setPaused(false);
      this.isPlaying = true;
    });

    this.input.on("pointerup", () => {
      if (this.isDraging) {
        this.isDraging = false;
        this.isPlaying = true;
        this.video.setPaused(false);
      }
    });

    this.slider = this.plugins.get("rexSlider").add(this.sliderButton, {
      endPoints: [
        { x: this.sliderLine.x - SLIDER_HALF_WIDTH, y: this.sliderLine.y },
        { x: this.sliderLine.x + SLIDER_HALF_WIDTH, y: this.sliderLine.y },
      ],
      value: 0,
      enable: true,
      valuechangeCallback: (value) => {
        if (this.isDraging) {
          // console.log("Slider value", value);
          this.video.seekTo(value);
        }
      },
    });

    // Buffering pour la vidéo
    this.bufferBar = this.add
      .rexRoundRectangle(
        this.width / 2 - 764,
        this.height - 50,
        0,
        6,
        3,
        Colors.Elmt.Gray
      )
      .setOrigin(0, 0.5)
      .setAlpha(0.4)
      .setDepth(20)
      .setVisible(false);
    this.allControls.push(this.bufferBar);

    // Affiche les contrôles après le démarrage
    this.video.on("play", () => {
      this.allControls.forEach((control) => control.setVisible(true));
      this.bufferBar.setVisible(true);
      this.startButton.setAlpha(0);
    });

    // Met à jour la barre de buffering à chaque frame
    this.video.on("progress", () => {
      // getVideoBuffered() retourne un tableau de ranges [{start, end}]
      const buffered =
        this.video.getVideoBuffered && this.video.getVideoBuffered();
      const duration = this.video.getDuration();
      if (buffered && buffered.length > 0 && duration) {
        // On prend la fin du premier range (le plus avancé)
        const end = buffered[buffered.length - 1].end;
        const percent = Phaser.Math.Clamp(end / duration, 0, 1);
        this.bufferBar.width = percent * this.SLIDER_WIDTH;
      }
    });
    // 📥 Sur fin de la vidéo
    let hasEnded = false;

    // Rounded button terminer
    this.buttonTerminer = this.add
      .existing(
        new ButtonRounded(
          this,
          this.width - 330 / 2 - 60,
          this.height - 76 / 2 - 60,
          330,
          76,
          "CONTINUE",
          Colors.Text.Gray,
          Colors.Elmt.Yellow,
          true,
          Colors.Elmt.Yellow,
          true,
          false,
          "fleche",
          ""
        )
      )
      .setDepth(101)
      .setVisible(false); // Set a higher depth to ensure it is above collapses and fog

    this.video.on("complete", () => {
      hasEnded = true;
      blocker.setVisible(true);
      this.cameras.main.fadeOut(500, 0, 0, 0); // Fade out over 500ms (black)
      this.cameras.main.once("camerafadeoutcomplete", () => {
        this.scene.restart();
        if (this.game.data.activeIndex < 1) {
          this.game.data.activeIndex = 1;
        }
        this.game.data.validatedIndex = this.game.data.activeIndex;
        this.scene.start("Menu");
      });
      /*  this.buttonTerminer.setVisible(true); */
      /* this.allControls.forEach((control) => control.setVisible(false)); */
    });

    // Clique sur la vidéo pour la mettre en pause ou reprendre
    this.playPauseButton.setInteractive({ useHandCursor: true });
    this.playPauseButton.on("pointerdown", () => {
      if (hasEnded) return;
      // Si la vidéo est en pause, la reprendre
      if (this.video.isPlaying()) {
        this.video.pause();
        this.isPlaying = false;
        this.playPauseButton.setTexture("play");
      } else {
        this.video.resume();
        this.startButton.setAlpha(0);
        this.isPlaying = true;
        this.playPauseButton.setTexture("pause");
      }
    });

    this.buttonTerminer.on("pointerdown", () => {
      this.scene.restart();
      if (this.game.data.activeIndex < 1) {
        this.game.data.activeIndex = 1;
      }
      this.game.data.validatedIndex = this.game.data.activeIndex;
      this.scene.start("Menu");
    });
  }
}
